import { AlertTriangle, RefreshCw, Users } from 'lucide-react'
import { useState } from 'react'
import { useCustomers, useMe, useSyncRuns, useTeams, useTriggerSync } from '../../api/hooks'
import { Badge, Button, Card, Input, PageHeader, Spinner, Stat, TableSkeleton } from '../../components/ui'
import { useToast } from '../../components/ui/toast'
import { CUSTOMER_TYPE_LABEL, fmtDateTime, fmtRelative, SYNC_STATUS_LABEL } from '../../lib/format'

const SYNC_TONE = { OK: 'success', VANTAR: 'warning', FEL: 'danger' } as const

export function TeamsPage() {
  const me = useMe()
  const teams = useTeams()
  const runs = useSyncRuns()
  const sync = useTriggerSync()
  const toast = useToast()
  const [selected, setSelected] = useState<string | undefined>(undefined)
  const [q, setQ] = useState('')
  const customers = useCustomers(q, selected)
  const isManager = me.data?.role !== 'RADGIVARE'
  const list = teams.data ?? []
  const activeTeams = list.filter((t) => t.is_active)
  const kund = list.reduce((a, t) => a + t.kund_count, 0)
  const prospekt = list.reduce((a, t) => a + t.prospekt_count, 0)
  const last = runs.data?.[0]

  return (
    <>
      <PageHeader
        title={isManager ? 'Team & kunder' : 'Mitt team'}
        subtitle={isManager ? 'Speglar Dynamics 365. Synkas automatiskt varje natt kl. 00.00.' : 'Dina kunder och prospekt enligt Dynamics 365.'}
        actions={isManager && (
          <Button variant="primary" icon={<RefreshCw className="size-4" />} loading={sync.isPending} onClick={() => sync.mutate(undefined, { onSuccess: (r) => toast.push(r.status === 'OK' ? 'success' : 'error', r.status === 'OK' ? `Synk klar: ${r.teams_upserted} team, ${r.customers_upserted} kunder` : `Synk misslyckades: ${r.error}`) })}>
            Synka från Dynamics nu
          </Button>
        )}
      />

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Rådgivarteam" value={activeTeams.length} sub={list.length !== activeTeams.length ? `${list.length - activeTeams.length} inaktiva` : 'aktiva'} tone="navy" />
        <Stat label="Kunder" value={kund} delay={40} />
        <Stat label="Prospekt" value={prospekt} delay={80} />
        <Stat label="Senaste synk" delay={120} value={last ? <Badge tone={SYNC_TONE[last.status]} dot>{SYNC_STATUS_LABEL[last.status]}</Badge> : '–'} sub={last ? `${fmtRelative(last.started_at)} · ${last.trigger === 'MANUELL' ? `manuell (${last.triggered_by_name ?? '–'})` : 'schemalagd'}` : ''} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Card padded={false} title="Team" actions={<span className="text-xs text-ink-500">{list.length} st</span>}>
          {teams.isLoading ? <TableSkeleton rows={10} cols={6} /> : (
            <div className="max-h-[60vh] overflow-auto">
              <table className="w-full">
                <thead className="table-head sticky top-0 bg-card"><tr><th>Kod</th><th>Team</th><th>Rådgivare</th><th className="text-right">Kund</th><th className="text-right">Prospekt</th><th>Synk</th></tr></thead>
                <tbody>
                  {list.map((t) => (
                    <tr key={t.id} onClick={() => setSelected(selected === t.id ? undefined : t.id)} className={`table-row cursor-pointer ${selected === t.id ? 'bg-navy-50' : ''} ${!t.is_active ? 'opacity-50' : ''}`}>
                      <td className="font-mono text-xs">{t.code}</td>
                      <td className="font-medium text-ink-900">{t.name}{!t.is_active && <Badge tone="warning" className="ml-2">Borttaget i Dynamics</Badge>}</td>
                      <td className="text-ink-500">{t.advisor_name}<span className="block text-xs text-ink-400">{t.office}</span></td>
                      <td className="text-right tabular-nums">{t.kund_count}</td>
                      <td className="text-right tabular-nums">{t.prospekt_count}</td>
                      <td>
                        <span title={t.last_synced_at ? fmtDateTime(t.last_synced_at) : ''}><Badge tone={SYNC_TONE[t.sync_status]} dot>{SYNC_STATUS_LABEL[t.sync_status]}</Badge></span>
                        {t.sync_error && <span title={t.sync_error}><AlertTriangle className="ml-1 inline size-3.5 text-danger-500" /></span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="space-y-4">
          <Card padded={false} title={<div className="flex items-center gap-2"><Users className="size-4 text-ink-400" /><h3 className="text-sm font-semibold">{selected ? `Kunder – ${list.find((t) => t.id === selected)?.code}` : 'Kunder & prospekt'}</h3></div>} actions={<Input placeholder="Sök…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 w-48 text-xs" />}>
            {customers.isLoading ? <Spinner /> : (
              <div className="max-h-[42vh] overflow-auto">
                <table className="w-full">
                  <thead className="table-head sticky top-0 bg-card"><tr><th>Namn</th><th>E-post</th><th>Team</th><th>Typ</th></tr></thead>
                  <tbody>
                    {customers.data?.map((c) => (
                      <tr key={c.id} className="table-row">
                        <td className="font-medium text-ink-900">{c.first_name} {c.last_name}{c.unsubscribed && <Badge tone="neutral" className="ml-2">Avregistrerad</Badge>}</td>
                        <td className="text-ink-500">{c.email}</td>
                        <td className="font-mono text-xs text-ink-500">{c.team_code}</td>
                        <td><Badge tone={c.customer_type === 'KUND' ? 'success' : 'copper'}>{CUSTOMER_TYPE_LABEL[c.customer_type]}</Badge></td>
                      </tr>
                    ))}
                    {customers.data?.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-sm text-ink-500">Inga träffar.</td></tr>}
                  </tbody>
                </table>
                {customers.data && customers.data.length >= 100 && <p className="px-3 py-2 text-xs text-ink-400">Visar de första 100 – förfina sökningen.</p>}
              </div>
            )}
          </Card>

          {isManager && (
            <Card padded={false} title="Synkhistorik">
              <div className="max-h-56 overflow-auto">
                <table className="w-full">
                  <thead className="table-head"><tr><th>Tid</th><th>Typ</th><th>Status</th><th className="text-right">Team</th><th className="text-right">Kunder</th></tr></thead>
                  <tbody>
                    {runs.data?.map((r) => (
                      <tr key={r.id} className="table-row">
                        <td className="tabular-nums text-ink-500">{fmtDateTime(r.started_at)}</td>
                        <td>{r.trigger === 'MANUELL' ? `Manuell · ${r.triggered_by_name ?? '–'}` : 'Schemalagd'}</td>
                        <td><Badge tone={SYNC_TONE[r.status]} dot>{SYNC_STATUS_LABEL[r.status]}</Badge>{r.error && <span className="ml-2 text-xs text-danger-600">{r.error}</span>}</td>
                        <td className="text-right tabular-nums">{r.teams_upserted}{r.teams_deactivated ? <span className="text-warning-600"> (−{r.teams_deactivated})</span> : ''}</td>
                        <td className="text-right tabular-nums">{r.customers_upserted}{r.customers_deactivated ? <span className="text-warning-600"> (−{r.customers_deactivated})</span> : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      </div>
    </>
  )
}
